import React from "react";
import { Spinner } from "./Spinner.js";

interface TableColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyField: string;
  emptyMessage?: string;
  loading?: boolean;
}

export function Table<T extends Record<string, unknown>>({
  columns,
  data,
  keyField,
  emptyMessage = "No data",
  loading = false,
}: TableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider ${col.className ?? ""}`}
                style={{ color: "var(--c-text-faint)" }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={String(row[keyField])}
              className="transition-colors duration-200"
              style={{
                borderBottom: i < data.length - 1 ? "1px solid var(--c-active)" : undefined,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-3 ${col.className ?? ""}`}
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  {col.render ? col.render(row) : String(row[col.key] ?? "")}
                </td>
              ))}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center"
                style={{ color: "var(--c-text-faint)" }}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Spinner size={14} />
                    Loading...
                  </div>
                ) : (
                  emptyMessage
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
